import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "sonner";

const DEMO = [
  { email: "admin@shariti.fr", label: "Admin" },
  { email: "anastasia@shariti.fr", label: "CDP Anastasia" },
  { email: "selvarajah@shariti.fr", label: "CDP Selvarajah" },
  { email: "chef@shariti.fr", label: "Chef d'équipe" },
];

// Le panneau d'aide (dev) ne doit JAMAIS apparaître sur le domaine public de production.
// On se base sur le nom de domaine au RUNTIME (fiable, indépendant du build et du .env) :
// visible uniquement en preview (*.preview.*) ou en local, masqué partout ailleurs (prod).
function isDevEnvironment() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname || "";
  return h === "localhost" || h === "127.0.0.1" || h.includes(".preview.");
}
const SHOW_DEMO = isDevEnvironment();

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const year = new Date().getFullYear();

  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`Bienvenue, ${u.name}`);
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Visuel (desktop uniquement) */}
      <div className="relative hidden overflow-hidden lg:block" style={{ background: "linear-gradient(140deg, #EE722E 0%, #F89F63 55%, #FCDB53 130%)" }}>
        <div className="absolute inset-0 opacity-[0.15]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 0, transparent 45%), radial-gradient(circle at 80% 70%, white 0, transparent 40%)" }} />
        <div className="relative flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/shariti-logo.webp" alt="Shariti" className="h-14 w-14 rounded-2xl bg-white/90 p-1.5 object-contain" />
            <div>
              <div className="text-2xl font-extrabold tracking-tight">SHARITI</div>
              <div className="text-[11px] font-light tracking-[0.3em]">FACE TO FACE</div>
            </div>
          </div>
          <div>
            <motion.h2 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="max-w-md text-4xl font-extrabold leading-tight tracking-tight">
              Pilotez vos partenariats terrain, du spot au bulletin.
            </motion.h2>
            <p className="mt-4 max-w-md text-base font-light text-white/90">
              Banque de spots, planning des lignes, prospection assistée et décisions guidées par les données — en un seul endroit.
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm font-light text-white/80">
            <ShieldCheck className="h-4 w-4" /> Plateforme interne sécurisée
          </div>
        </div>
      </div>

      {/* Formulaire */}
      <div className="flex flex-col bg-white px-6 py-8">
        {/* Logo (mobile uniquement) */}
        <div className="lg:hidden">
          <img src="/shariti-logo.webp" alt="Shariti" className="h-12 w-12 object-contain" />
        </div>

        {/* Bloc de connexion, centré verticalement */}
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-sm">
            <h1 className="text-2xl font-extrabold tracking-tight">Connexion</h1>
            <p className="mt-1 text-sm text-muted-ink">Accédez à votre espace Shariti.</p>

            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs font-semibold">Adresse email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@shariti.fr" className="mt-1.5 h-11" data-testid="login-email" />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs font-semibold">Mot de passe</Label>
                <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="mt-1.5" inputClassName="h-11" data-testid="login-password" />
              </div>
              <Button type="submit" disabled={loading} data-testid="login-submit"
                className="h-11 w-full bg-[var(--shariti-orange)] text-white hover:bg-[#E56622]">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Se connecter <ArrowRight className="ml-1 h-4 w-4" /></>}
              </Button>
            </form>

            <p className="mt-6 text-center text-xs text-muted-ink" data-testid="login-help">
              Un problème de connexion ? Contactez votre administrateur.
            </p>

            {/* Panneau d'aide à la connexion (dev) : affiché UNIQUEMENT si explicitement activé
                par variable d'environnement (preview/dev). Absent en production. NE CONTIENT
                AUCUN mot de passe : seuls les emails sont pré-remplis, le mot de passe est saisi
                à la main -> aucun secret n'est embarqué dans le bundle JS. */}
            {SHOW_DEMO && (
              <div className="mt-8 rounded-xl border border-[var(--border-soft)] bg-[var(--surface-2)] p-4" data-testid="demo-accounts-panel">
                <div className="text-xs font-bold text-muted-ink">Connexion rapide (dev)</div>
                <p className="mt-1 text-[11px] text-muted-ink">Pré-remplit l'email — saisissez le mot de passe manuellement.</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {DEMO.map((d) => (
                    <button key={d.email} type="button" data-testid={`demo-${d.label.toLowerCase().replace(/[^a-z]/g,'')}`}
                      onClick={() => setEmail(d.email)}
                      className="rounded-lg border border-[var(--border-soft)] bg-white px-2.5 py-1 text-[11px] font-semibold hover:border-[var(--shariti-orange)]">
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pied de page léger */}
        <footer className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 pt-6 text-[11px] text-muted-ink" data-testid="login-footer">
          <img src="/shariti-logo.webp" alt="" aria-hidden="true" className="h-4 w-4 object-contain opacity-80" />
          <span className="font-semibold text-ink">Shariti</span>
          <span aria-hidden="true">·</span>
          <span>Plateforme interne sécurisée</span>
          <span aria-hidden="true">·</span>
          <span>© {year}</span>
        </footer>
      </div>
    </div>
  );
}
